import { MainLayout } from '@/components/layouts/MainLayout'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import {
  StatsOverview,
  BadgesGrid,
  LeaderboardTable,
  PointsChart,
  RecentActivity
} from '@/components/account'
import { Link } from '@inertiajs/react'
import { Settings, Shield, Users, Award, FileText, HelpCircle, Mail } from 'lucide-react'
import type {
  AccountUser,
  AccountStats,
  AccountBadge,
  AccountLeaderboardEntry,
  AccountActivity
} from '@/types'

interface Props {
  user: AccountUser
  stats: AccountStats
  badges: AccountBadge[]
  leaderboard: AccountLeaderboardEntry[]
  recent_activity: AccountActivity[]
}

export default function AccountShow({
  user,
  stats,
  badges,
  leaderboard,
  recent_activity
}: Props) {
  return (
    <MainLayout title="Mon compte">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-display font-bold text-gray-900">
              {user.name}
            </h1>
            <p className="text-gray-500">
              {user.specialty && user.rpps && <>{user.specialty} &bull; RPPS {user.rpps}</>}
              {user.specialty && !user.rpps && <>{user.specialty}</>}
              {!user.specialty && user.rpps && <>RPPS {user.rpps}</>}
              {user.role === 'admin' && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  <Shield size={12} className="mr-1" />
                  Administrateur
                </span>
              )}
            </p>
          </div>
          <Link href="/account/edit">
            <Button variant="outline">
              <Settings size={16} className="mr-2" />
              Modifier le profil
            </Button>
          </Link>
        </div>

        {/* Admin Section */}
        {user.role === 'admin' && (
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500 rounded-lg text-white">
                  <Shield size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Administration</h2>
                  <p className="text-sm text-gray-600">Gérer la plateforme Tessan</p>
                </div>
              </div>
              <Link href="/admin">
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-white">
                  Accéder au tableau de bord
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link
                href="/admin/users"
                className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
              >
                <Users size={18} className="text-blue-500" />
                <span className="text-sm font-medium text-gray-700">Utilisateurs</span>
              </Link>
              <Link
                href="/admin/badges"
                className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
              >
                <Award size={18} className="text-purple-500" />
                <span className="text-sm font-medium text-gray-700">Badges</span>
              </Link>
              <Link
                href="/admin/categories"
                className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
              >
                <FileText size={18} className="text-green-500" />
                <span className="text-sm font-medium text-gray-700">Catégories</span>
              </Link>
              <Link
                href="/admin/faq_items"
                className="flex items-center gap-2 p-3 bg-white rounded-lg hover:shadow-md transition-shadow"
              >
                <HelpCircle size={18} className="text-orange-500" />
                <span className="text-sm font-medium text-gray-700">FAQ</span>
              </Link>
            </div>
          </Card>
        )}

        {/* Stats overview */}
        <StatsOverview stats={stats} />

        {/* Main content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Points chart */}
            <Card>
              <h2 className="text-xl font-semibold mb-4">
                Progression (30 jours)
              </h2>
              <PointsChart data={stats.points_history} />
            </Card>

            {/* Badges */}
            <Card>
              <h2 className="text-xl font-semibold mb-4">
                Mes badges ({badges.filter(b => b.earned).length}/{badges.length})
              </h2>
              <BadgesGrid badges={badges} />
            </Card>
          </div>

          {/* Right column */}
          <div className="space-y-8">
            {/* Leaderboard */}
            <Card>
              <h2 className="text-xl font-semibold mb-4">Classement</h2>
              <LeaderboardTable entries={leaderboard} />
            </Card>

            {/* Recent activity */}
            <Card>
              <h2 className="text-xl font-semibold mb-4">Activité récente</h2>
              <RecentActivity transactions={recent_activity} />
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
